/**
 * Helpers for redirecting Document Extraction V2 "AI Summary" citations to the
 * source document they were derived from.
 *
 * The summary artifact is a `DocumentReference` whose only content is an inline
 * base64 `text/markdown` attachment (no file-service URL). When an agent cites
 * the artifact directly, the AR resolves it but has nothing viewable to render,
 * so the citation shows up as a report with nothing attached.
 *
 * Newly generated reports cite the source document instead (fixed agent-side),
 * but already-generated `ClinicalImpression`s still reference the artifact. The
 * artifact is self-describing — it carries the document-type coding below and a
 * `relatesTo[transforms]` link back to the original report — so the attachment
 * arbiter can detect it and swap in the source document at render time.
 */

const LIFEOMIC_DOCUMENT_TYPE_SYSTEM = 'http://lifeomic.com/fhir/document-type';

export const DOCUMENT_EXTRACTION_V2_SUMMARY_DOCUMENT_TYPE =
  'document-extraction-v2-summary';

/**
 * True when the resource is a Document Extraction V2 AI Summary artifact (the
 * markdown-only DocumentReference that renders empty when cited directly).
 */
export const isDocumentExtractionV2SummaryArtifact = (
  resource: fhir.Resource | undefined,
): resource is fhir.DocumentReference => {
  if (!resource || resource.resourceType !== 'DocumentReference') {
    return false;
  }
  const docRef = resource as fhir.DocumentReference;
  return Boolean(
    docRef.type?.coding?.some(
      coding =>
        coding.system === LIFEOMIC_DOCUMENT_TYPE_SYSTEM &&
        coding.code === DOCUMENT_EXTRACTION_V2_SUMMARY_DOCUMENT_TYPE,
    ),
  );
};

/**
 * Returns the source `DocumentReference/{id}` reference a summary artifact was
 * derived from (its `relatesTo[code='transforms']` target), or `undefined` when
 * the link is missing or does not point at a DocumentReference.
 */
export const getSummaryArtifactSourceReference = (
  artifact: fhir.DocumentReference,
): string | undefined => {
  for (const relation of artifact.relatesTo ?? []) {
    if (relation.code !== 'transforms') {
      continue;
    }
    const reference = relation.target?.reference;
    if (
      typeof reference === 'string' &&
      reference.startsWith('DocumentReference/')
    ) {
      return reference;
    }
  }
  return undefined;
};
